import { useState, useEffect, useRef, useCallback } from "react";
import {
  Trash2,
  Upload,
  Loader2,
  FileText,
  XCircle,
  ChevronDown,
  X,
  Save,
  ArrowUp,
  Square,
} from "lucide-react";
import api from "../api/api";
import MainLayout from "../sidebar/Sidebar.js";
import Select from "react-select";
import { motion, AnimatePresence } from "framer-motion";
import {
  PromptInput,
  PromptInputTextarea,
  PromptInputActions,
  PromptInputAction,
} from "../components/prompt-kit/prompt-input";
import { Button } from "../components/ui/button";

export default function Estimate() {
  const [input, setInput] = useState("");
  const [lineItems, setLineItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [exportMessage, setExportMessage] = useState("");
  const [error, setError] = useState("");
  const [location, setLocation] = useState("Estimate");
  const [existingProjects, setExistingProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [isCreatingNewProject, setIsCreatingNewProject] = useState(false);
  const [isTypingInAI, setIsTypingInAI] = useState(false);
  const [manualEntry, setManualEntry] = useState({
    name: "",
    code: "",
    quantity: "",
    unit: "",
    unitPrice: "",
    category: "",
  });
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  
  // Template search state
  const [templateSearchQuery, setTemplateSearchQuery] = useState("");
  const [templateSearchResults, setTemplateSearchResults] = useState([]);
  const [isSearchingTemplates, setIsSearchingTemplates] = useState(false);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(-1);
  const [debounceDelay, setDebounceDelay] = useState(300); // Configurable: 300ms default, can be set to 3000ms
  const templateInputRef = useRef(null);
  const dropdownRef = useRef(null);
  
  // Code search state
  const [codeSearchQuery, setCodeSearchQuery] = useState("");
  const [codeSearchResults, setCodeSearchResults] = useState([]);
  const [isSearchingCodes, setIsSearchingCodes] = useState(false);
  const [showCodeDropdown, setShowCodeDropdown] = useState(false);
  const [selectedCodeIndex, setSelectedCodeIndex] = useState(-1);
  const codeInputRef = useRef(null);
  const codeDropdownRef = useRef(null);

  const projectOptions = existingProjects.map((project) => ({
    value: project.id,
    label: project.name,
  }));

  useEffect(() => {
    async function fetchProjects() {
      try {
        const res = await api.get("/projects/");
        setExistingProjects(res.data);
      } catch (err) {
        console.error("Failed to fetch existing projects", err);
      }
    }
    fetchProjects();
  }, []);

  // Debounced template search function
  const searchTemplates = useCallback(async (query) => {
    if (query.length < 2) {
      setTemplateSearchResults([]);
      setShowTemplateDropdown(false);
      return;
    }

    setIsSearchingTemplates(true);
    try {
      const res = await api.get("/line-item-templates/search", {
        params: { q: query, limit: 20 },
      });
      setTemplateSearchResults(res.data || []);
      setShowTemplateDropdown(true);
      setSelectedTemplateIndex(-1);
    } catch (err) {
      console.error("Failed to search templates", err);
      setTemplateSearchResults([]);
    } finally {
      setIsSearchingTemplates(false);
    }
  }, []);

  // Sync templateSearchQuery with manualEntry.name
  useEffect(() => {
    setTemplateSearchQuery(manualEntry.name);
  }, [manualEntry.name]);

  // Debounce effect for template search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (templateSearchQuery.trim().length >= 2) {
        searchTemplates(templateSearchQuery.trim());
      } else {
        setTemplateSearchResults([]);
        setShowTemplateDropdown(false);
      }
    }, debounceDelay);

    return () => clearTimeout(timer);
  }, [templateSearchQuery, debounceDelay, searchTemplates]);

  // Handle template selection
  const handleTemplateSelect = (template) => {
    const updatedEntry = {
      name: template.name,
      code: template.code || "",
      quantity: "",
      unit: template.unit || "",
      unitPrice: template.unit_price || "",
      category: template.category || "",
    };
    setManualEntry(updatedEntry);
    setTemplateSearchQuery(template.name); // Keep the name visible
    setShowTemplateDropdown(false);
    setSelectedTemplateIndex(-1);
    
    // Increment template usage count
    if (template.id) {
      api.post(`/line-item-templates/${template.id}/use`).catch((err) => {
        console.error("Failed to update template usage", err);
      });
    }
  };

  // Handle keyboard navigation in template dropdown
  const handleTemplateKeyDown = (e) => {
    if (!showTemplateDropdown || templateSearchResults.length === 0) {
      if (e.key === "Enter" && templateSearchQuery.trim()) {
        // Allow manual entry if no dropdown is shown
        return;
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedTemplateIndex((prev) =>
          prev < templateSearchResults.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedTemplateIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedTemplateIndex >= 0 && selectedTemplateIndex < templateSearchResults.length) {
          handleTemplateSelect(templateSearchResults[selectedTemplateIndex]);
        } else if (templateSearchResults.length > 0) {
          // Select first item if none selected
          handleTemplateSelect(templateSearchResults[0]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setShowTemplateDropdown(false);
        setSelectedTemplateIndex(-1);
        templateInputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  // Debounced code search function
  const searchCodes = useCallback(async (query) => {
    if (query.length < 2) {
      setCodeSearchResults([]);
      setShowCodeDropdown(false);
      return;
    }

    setIsSearchingCodes(true);
    try {
      const res = await api.get("/line-item-templates/search", {
        params: { q: query, limit: 20 },
      });
      // Filter to only show templates that have a code matching the query
      const codesWithMatches = (res.data || []).filter(t => t.code && t.code.toLowerCase().includes(query.toLowerCase()));
      setCodeSearchResults(codesWithMatches);
      setShowCodeDropdown(codesWithMatches.length > 0);
      setSelectedCodeIndex(-1);
    } catch (err) {
      console.error("Failed to search codes", err);
      setCodeSearchResults([]);
    } finally {
      setIsSearchingCodes(false);
    }
  }, []);

  // Debounce effect for code search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (codeSearchQuery.trim().length >= 2) {
        searchCodes(codeSearchQuery.trim());
      } else {
        setCodeSearchResults([]);
        setShowCodeDropdown(false);
      }
    }, debounceDelay);

    return () => clearTimeout(timer);
  }, [codeSearchQuery, debounceDelay, searchCodes]);

  // Handle code selection
  const handleCodeSelect = (template) => {
    setManualEntry({
      ...manualEntry,
      code: template.code || "",
      name: template.name || manualEntry.name,
      unit: template.unit || manualEntry.unit,
      unitPrice: template.unit_price || manualEntry.unitPrice,
      category: template.category || manualEntry.category,
    });
    setCodeSearchQuery("");
    setShowCodeDropdown(false);
    setSelectedCodeIndex(-1);
  };

  // Handle keyboard navigation in code dropdown
  const handleCodeKeyDown = (e) => {
    if (!showCodeDropdown || codeSearchResults.length === 0) {
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedCodeIndex((prev) =>
          prev < codeSearchResults.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedCodeIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedCodeIndex >= 0 && selectedCodeIndex < codeSearchResults.length) {
          handleCodeSelect(codeSearchResults[selectedCodeIndex]);
        } else if (codeSearchResults.length > 0) {
          handleCodeSelect(codeSearchResults[0]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setShowCodeDropdown(false);
        setSelectedCodeIndex(-1);
        codeInputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Template dropdown
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        templateInputRef.current &&
        !templateInputRef.current.contains(event.target)
      ) {
        setShowTemplateDropdown(false);
        setSelectedTemplateIndex(-1);
      }
      
      // Code dropdown
      if (
        codeDropdownRef.current &&
        !codeDropdownRef.current.contains(event.target) &&
        codeInputRef.current &&
        !codeInputRef.current.contains(event.target)
      ) {
        setShowCodeDropdown(false);
        setSelectedCodeIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDelete = (index) => {
    const updated = lineItems.filter((_, i) => i !== index);
    setLineItems(updated);
  };

  const handleEditItem = (index) => {
    setEditingIndex(index);
    setEditingItem({ ...lineItems[index] });
    setShowEditPanel(true);
  };

  const handleSaveEdit = () => {
    if (!editingItem.name.trim()) {
      setError("Line item name cannot be empty");
      return;
    }

    const updatedItems = [...lineItems];
    updatedItems[editingIndex] = {
      ...editingItem,
      quantity: editingItem.quantity
        ? Number.parseInt(editingItem.quantity, 10)
        : 0,
    };
    setLineItems(updatedItems);
    setShowEditPanel(false);
    setEditingIndex(null);
    setEditingItem(null);
    setError("");
  };

  const handleCancelEdit = () => {
    setShowEditPanel(false);
    setEditingIndex(null);
    setEditingItem(null);
    setError("");
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError("");
    setExportMessage("");
    setIsTypingInAI(false);

    try {
      const res = await api.post("/generate-estimate", { input_text: input });
      const items = res.data.line_items || [];

      const aiItems = items.map((item) => ({ ...item, source: "ai" }));

      // Optional: Show one subtle "loading" animation while AI thinks
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Add all items in one batch update (no flicker)
      setLineItems((prev) => [...prev, ...aiItems]);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Failed to generate estimate. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetAll = () => {
    setLineItems([]);
    setInput("");
    setManualEntry({
      name: "",
      code: "",
      quantity: "",
      unit: "",
      unitPrice: "",
      category: "",
    });
    setTemplateSearchQuery("");
    setTemplateSearchResults([]);
    setShowTemplateDropdown(false);
    setCodeSearchQuery("");
    setCodeSearchResults([]);
    setShowCodeDropdown(false);
    setError("");
    setExportMessage("");
  };

  const handleExport = async () => {
    setExportLoading(true);
    setExportMessage("");
    setError("");

    try {
      let projectNameToSend = "";

      if (isCreatingNewProject) {
        if (!projectName.trim()) {
          setError("Please enter a new project name.");
          setExportLoading(false);
          return;
        }
        projectNameToSend = projectName.trim();
      } else {
        const selectedProject = existingProjects.find(
          (proj) => proj.id === selectedProjectId
        );

        if (!selectedProject) {
          setError("Please select a valid project to export.");
          setExportLoading(false);
          return;
        }
        projectNameToSend = selectedProject.name;
      }

      const res = await api.post("/export-line-items", {
        project_name: projectNameToSend,
        line_items: lineItems,
      });

      if (res.data.success) {
        setExportMessage(
          `Line items added successfully to project "${projectNameToSend}"`
        );
        handleResetAll();
        setShowExportModal(false);
        setProjectName("");
        setSelectedProjectId("");
        setIsCreatingNewProject(false);
      } else {
        setError(res.data.error || "Failed to export line items, try again.");
      }
    } catch (err) {
      setError(
        err.response?.data?.detail || "Failed to export line items, try again."
      );
    } finally {
      setExportLoading(false);
    }
  };

  const handleAddManualEntry = () => {
    if (!manualEntry.name.trim()) return;

    setLineItems([
      ...lineItems,
      {
        name: manualEntry.name,
        code: manualEntry.code,
        quantity: manualEntry.quantity
          ? Number.parseInt(manualEntry.quantity, 10)
          : 0,
        unit: manualEntry.unit,
        unitPrice: manualEntry.unitPrice,
        category: manualEntry.category,
        source: "manual",
      },
    ]);

    setManualEntry({
      name: "",
      code: "",
      quantity: "",
      unit: "",
      unitPrice: "",
      category: "",
    });
  };

  const isManualEntryDisabled = isTypingInAI;

  const aiItemCount = lineItems.filter((item) => item.source === "ai").length;
  const manualItemCount = lineItems.filter(
    (item) => item.source === "manual"
  ).length;

  return (
    <MainLayout location={location} setLocation={setLocation}>
      <div className="min-h-full bg-gray-50 py-4 px-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 text-sm">
              {error}
            </div>
          )}
          {exportMessage && (
            <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-4 text-sm">
              {exportMessage}
            </div>
          )}

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="space-y-2 mb-4">
              <label className="text-md font-light text-gray-900 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                AI Analysis
              </label>
              <p className="text-xs text-gray-500">
                Provide a list of all the items that you would like to add to
                your estimate
              </p>
            </div>

            <PromptInput
              value={input}
              onValueChange={(value) => {
                setInput(value);
                setIsTypingInAI(true);
              }}
              isLoading={loading}
              onSubmit={handleGenerate}
              className="w-full max-w-full"
            >
              <PromptInputTextarea
                placeholder="Example: 400 nails, 10 tons of cement, and 20 drywall sheets"
                onFocus={() => setIsTypingInAI(true)}
                onBlur={() => setIsTypingInAI(false)}
              />
              <PromptInputActions className="justify-end pt-2">
                <PromptInputAction
                  tooltip={loading ? "Stop generation" : input.trim() ? "Generate Estimate" : "Type to generate"}
                >
                  <button
                    onClick={handleGenerate}
                    disabled={loading || !input.trim()}
                    className="relative inline-flex items-center justify-center rounded-full text-primary-foreground shadow-sm disabled:pointer-events-none disabled:opacity-50 overflow-hidden"
                    style={{
                      width: input.trim() && !loading ? 180 : 36,
                      height: 36,
                      transition: "all 1.1s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    {/* Base gray background */}
                    <span
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: "#6b7280",
                      }}
                    />
                    {/* Blue overlay that sweeps from right to left */}
                    <span
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: "linear-gradient(to left, #2563eb 0%, #2563eb 100%)",
                        clipPath: input.trim() && !loading 
                          ? "inset(0 0 0 0)" 
                          : "inset(0 0 0 100%)",
                        transition: "clip-path 1.4s cubic-bezier(0.25, 0.1, 0.25, 1)",
                      }}
                    />
                    {/* Content container */}
                    <span className="relative z-10 flex items-center justify-center">
                      {/* Text that fades and slides in */}
                      <span
                        className="text-sm font-medium whitespace-nowrap overflow-hidden"
                        style={{
                          opacity: input.trim() && !loading ? 1 : 0,
                          maxWidth: input.trim() && !loading ? 120 : 0,
                          marginRight: input.trim() && !loading ? 8 : 0,
                          marginLeft: input.trim() && !loading ? 12 : 0,
                          transition: "all 1.1s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                      >
                        Generate Estimate
                      </span>
                      {/* Arrow icon */}
                      <span
                        className="flex items-center justify-center flex-shrink-0"
                        style={{
                          marginRight: input.trim() && !loading ? 12 : 0,
                          transition: "margin 1.1s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                      >
                        {loading ? (
                          <Square className="size-5 fill-current" />
                        ) : (
                          <ArrowUp className="size-5" />
                        )}
                      </span>
                    </span>
                  </button>
                </PromptInputAction>
              </PromptInputActions>
            </PromptInput>
          </div>

          <div className="bg-white rounded-lg border border-gray-200">
            <div className="flex items-center justify-between gap-3 p-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <span className="text-md font-light text-gray-900 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Estimate Results
                </span>
                {lineItems.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
                      {lineItems.length} total
                    </span>
                    {aiItemCount > 0 && (
                      <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-md">
                        {aiItemCount} AI
                      </span>
                    )}
                    {manualItemCount > 0 && (
                      <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-md">
                        {manualItemCount} manual
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {lineItems.length > 0 && (
                  <>
                    <button
                      onClick={handleResetAll}
                      className="flex items-center gap-2 px-3 py-2 border text-red-600 border-gray-300 rounded-md bg-white text-sm shadow-sm hover:bg-gray-50"
                    >
                      <XCircle className="w-4 h-4" />
                      Reset All
                    </button>
                    <button
                      onClick={() => setShowExportModal(true)}
                      className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm shadow-sm hover:bg-gray-50"
                    >
                      <Upload className="w-4 h-4 text-gray-600" />
                      Export All
                    </button>
                  </>
                )}
                <button
                  className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm shadow-sm hover:bg-gray-50"
                  onClick={() => {
                    /* Import functionality to be implemented */
                  }}
                >
                  <Upload className="w-4 h-4 text-gray-600" />
                  Import Template
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Line Item
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Qty
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Unit
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Unit Price
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <AnimatePresence>
                    {/* Always show existing items */}
                    {lineItems.map((item, index) => (
                      <motion.tr
                        key={`line-${index}`}
                        initial={{ opacity: 0, y: 10, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{
                          duration: 0.35,
                          ease: "easeOut",
                          delay: index * 0.07,
                        }}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => handleEditItem(index)}
                      >
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="flex items-center gap-2">
                            {item.name}
                            {item.source === "ai" && (
                              <span className="inline-flex px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
                                AI
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {item.code || "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {item.quantity || 0}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {item.unit || "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {item.unitPrice || "-"}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                            {item.category || "General"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(index);
                            }}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded-md transition-colors"
                            title="Delete item"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </motion.tr>
                    ))}

                    {/* Show placeholders only while loading */}
                    {loading && (
                      <tr>
                        <td
                          colSpan="7"
                          className="px-3 py-4 text-center text-sm text-gray-900"
                        >
                          <div className="flex items-center justify-center gap-3">
                            <Loader2 className="w-5 h-5 animate-spin text-gray-600" />
                            <span className="font-light">
                              Generating Estimate...
                            </span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </AnimatePresence>

                  <tr
                    className={`${
                      isManualEntryDisabled
                        ? "bg-gray-50 opacity-60"
                        : "bg-white hover:bg-gray-50"
                    } transition-colors`}
                  >
                    <td className="px-4 py-3 relative">
                      <div className="relative">
                        <input
                          ref={templateInputRef}
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                          placeholder="Search or type line item..."
                          value={manualEntry.name}
                          onChange={(e) => {
                            const value = e.target.value;
                            setTemplateSearchQuery(value);
                            setManualEntry({
                              ...manualEntry,
                              name: value,
                            });
                          }}
                          disabled={isManualEntryDisabled}
                          onKeyDown={(e) => {
                            handleTemplateKeyDown(e);
                            if (e.key === "Enter" && !showTemplateDropdown && manualEntry.name.trim()) {
                              handleAddManualEntry();
                            }
                          }}
                          onFocus={() => {
                            if (templateSearchQuery.trim().length >= 2) {
                              setShowTemplateDropdown(true);
                            }
                          }}
                        />
                        {isSearchingTemplates && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                          </div>
                        )}
                        
                        {/* Template Dropdown */}
                        {showTemplateDropdown && templateSearchResults.length > 0 && (
                          <div
                            ref={dropdownRef}
                            className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
                          >
                            {templateSearchResults.map((template, index) => (
                              <div
                                key={template.id}
                                className={`px-3 py-2 cursor-pointer text-sm ${
                                  index === selectedTemplateIndex
                                    ? "bg-blue-50 text-blue-900"
                                    : "text-gray-900 hover:bg-gray-50"
                                }`}
                                onClick={() => handleTemplateSelect(template)}
                                onMouseEnter={() => setSelectedTemplateIndex(index)}
                              >
                                <div className="font-medium">
                                  {template.name}
                                  {template.code && (
                                    <span className="text-gray-500 ml-2">
                                      - {template.code}
                                    </span>
                                  )}
                                </div>
                                {(template.unit || template.category) && (
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    {template.unit && <span>{template.unit}</span>}
                                    {template.unit && template.category && <span> • </span>}
                                    {template.category && <span>{template.category}</span>}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 relative">
                      <div className="relative">
                        <input
                          ref={codeInputRef}
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                          placeholder="Code"
                          value={manualEntry.code}
                          onChange={(e) => {
                            const value = e.target.value;
                            setCodeSearchQuery(value);
                            setManualEntry({
                              ...manualEntry,
                              code: value,
                            });
                          }}
                          disabled={isManualEntryDisabled}
                          onKeyDown={(e) => {
                            handleCodeKeyDown(e);
                            if (e.key === "Enter" && !showCodeDropdown && manualEntry.name.trim()) {
                              handleAddManualEntry();
                            }
                          }}
                          onFocus={() => {
                            if (codeSearchQuery.trim().length >= 2) {
                              setShowCodeDropdown(true);
                            }
                          }}
                        />
                        {isSearchingCodes && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                          </div>
                        )}
                        
                        {/* Code Dropdown */}
                        {showCodeDropdown && codeSearchResults.length > 0 && (
                          <div
                            ref={codeDropdownRef}
                            className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
                          >
                            {codeSearchResults.map((template, index) => (
                              <div
                                key={template.id}
                                className={`px-3 py-2 cursor-pointer text-sm ${
                                  index === selectedCodeIndex
                                    ? "bg-blue-50 text-blue-900"
                                    : "text-gray-900 hover:bg-gray-50"
                                }`}
                                onClick={() => handleCodeSelect(template)}
                                onMouseEnter={() => setSelectedCodeIndex(index)}
                              >
                                <div className="font-medium">
                                  {template.code}
                                  {template.name && (
                                    <span className="text-gray-500 ml-2">
                                      - {template.name}
                                    </span>
                                  )}
                                </div>
                                {(template.unit || template.category) && (
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    {template.unit && <span>{template.unit}</span>}
                                    {template.unit && template.category && <span> • </span>}
                                    {template.category && <span>{template.category}</span>}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                        placeholder="0"
                        value={manualEntry.quantity}
                        onChange={(e) =>
                          setManualEntry({
                            ...manualEntry,
                            quantity: e.target.value,
                          })
                        }
                        disabled={isManualEntryDisabled}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleAddManualEntry();
                        }}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                        placeholder="Unit"
                        value={manualEntry.unit}
                        onChange={(e) =>
                          setManualEntry({
                            ...manualEntry,
                            unit: e.target.value,
                          })
                        }
                        disabled={isManualEntryDisabled}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleAddManualEntry();
                        }}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                        placeholder="$0.00"
                        value={manualEntry.unitPrice}
                        onChange={(e) =>
                          setManualEntry({
                            ...manualEntry,
                            unitPrice: e.target.value,
                          })
                        }
                        disabled={isManualEntryDisabled}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleAddManualEntry();
                        }}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="relative inline-flex items-center border border-gray-300 rounded-md bg-white shadow-sm text-sm hover:bg-gray-50">
                        <select
                          className="appearance-none pr-8 pl-3 py-2 bg-transparent text-gray-900 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                          value={manualEntry.category}
                          onChange={(e) =>
                            setManualEntry({
                              ...manualEntry,
                              category: e.target.value,
                            })
                          }
                          disabled={isManualEntryDisabled}
                        >
                          <option value="">Select type</option>
                          <option value="Material">Material</option>
                          <option value="Labor">Labor</option>
                          <option value="Equipment">Equipment</option>
                        </select>
                        <ChevronDown className="absolute right-2 w-4 h-4 text-gray-600 pointer-events-none" />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={handleAddManualEntry}
                        disabled={
                          isManualEntryDisabled || !manualEntry.name.trim()
                        }
                        className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        Add
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        <AnimatePresence>
          {showEditPanel && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 bg-black bg-opacity-30 z-40"
                onClick={handleCancelEdit}
              />

              {/* Side Panel */}
              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 overflow-y-auto"
              >
                <div className="p-6 space-y-6">
                  {/* Header */}
                  <div className="flex items-center justify-between border-b border-gray-200 pb-4">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Edit Line Item
                    </h2>
                    <button
                      onClick={handleCancelEdit}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Form Fields */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Line Item Name *
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter line item name"
                        value={editingItem?.name || ""}
                        onChange={(e) =>
                          setEditingItem({
                            ...editingItem,
                            name: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Code
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter code"
                        value={editingItem?.code || ""}
                        onChange={(e) =>
                          setEditingItem({
                            ...editingItem,
                            code: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quantity
                      </label>
                      <input
                        type="number"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0"
                        value={editingItem?.quantity || ""}
                        onChange={(e) =>
                          setEditingItem({
                            ...editingItem,
                            quantity: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Unit
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., pcs, kg, m"
                        value={editingItem?.unit || ""}
                        onChange={(e) =>
                          setEditingItem({
                            ...editingItem,
                            unit: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Unit Price
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="$0.00"
                        value={editingItem?.unitPrice || ""}
                        onChange={(e) =>
                          setEditingItem({
                            ...editingItem,
                            unitPrice: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category
                      </label>
                      <div className="relative">
                        <select
                          className="w-full appearance-none px-3 py-2 pr-10 border border-gray-300 rounded-md bg-white text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          value={editingItem?.category || ""}
                          onChange={(e) =>
                            setEditingItem({
                              ...editingItem,
                              category: e.target.value,
                            })
                          }
                        >
                          <option value="">Select type</option>
                          <option value="Material">Material</option>
                          <option value="Labor">Labor</option>
                          <option value="Equipment">Equipment</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none" />
                      </div>
                    </div>

                    {editingItem?.source && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Source
                        </label>
                        <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-600">
                          {editingItem.source === "ai" ? (
                            <span className="inline-flex items-center gap-2">
                              <span className="inline-flex px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
                                AI
                              </span>
                              AI Generated
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-2">
                              <span className="inline-flex px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">
                                Manual
                              </span>
                              Manually Added
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={handleSaveEdit}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors shadow-sm"
                    >
                      <Save className="w-4 h-4" />
                      Save Changes
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="flex-1 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-md transition-colors border border-gray-300 shadow-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {showExportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md border border-gray-200">
              <h2 className="text-lg font-semibold mb-4 text-gray-900">
                Export to Project
              </h2>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Choose Project
                  </label>
                  <div className="flex gap-4 mb-3">
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="radio"
                        name="projectOption"
                        value="existing"
                        checked={!isCreatingNewProject}
                        onChange={() => {
                          setIsCreatingNewProject(false);
                          setSelectedProjectId("");
                          setProjectName("");
                          setError("");
                        }}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-gray-700">Existing Project</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="radio"
                        name="projectOption"
                        value="new"
                        checked={isCreatingNewProject}
                        onChange={() => {
                          setIsCreatingNewProject(true);
                          setSelectedProjectId("");
                          setProjectName("");
                          setError("");
                        }}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-gray-700">New Project</span>
                    </label>
                  </div>

                  {isCreatingNewProject ? (
                    <input
                      type="text"
                      className="w-full p-3 border border-gray-300 rounded-md bg-white text-gray-900 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="Enter new project name"
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      autoFocus
                    />
                  ) : (
                    <Select
                      options={projectOptions}
                      value={
                        projectOptions.find(
                          (p) => p.value === selectedProjectId
                        ) || null
                      }
                      onChange={(selectedOption) =>
                        setSelectedProjectId(
                          selectedOption ? selectedOption.value : ""
                        )
                      }
                      placeholder="Search or select a project..."
                      isClearable
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  )}

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-3 text-sm mt-2">
                      {error}
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    className="flex-1 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-md text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    onClick={handleExport}
                    disabled={
                      exportLoading ||
                      (isCreatingNewProject
                        ? !projectName.trim()
                        : !selectedProjectId)
                    }
                  >
                    {exportLoading ? "Exporting..." : "Export"}
                  </button>
                  <button
                    className="flex-1 bg-white hover:bg-gray-50 px-4 py-2 rounded-md text-gray-700 text-sm font-medium transition-colors border border-gray-300 shadow-sm disabled:opacity-50"
                    onClick={() => {
                      setShowExportModal(false);
                      setError("");
                    }}
                    disabled={exportLoading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
